/**
 * How long something has been going on, in words a person says out loud.
 *
 * Two formats, and the difference between them is who is reading. `humanDuration` is the headline
 * on the all-clear banner and the one in the screen-reader announcement — a rounded, two-unit
 * summary, because "1 h 30 min" is a story and "5 402 seconds" is a log line. `liveDuration` is
 * the clock ticking inside the incident drawer, which has to move every second or it is not a
 * clock, so it keeps the seconds all the way up.
 *
 * There is no `Intl.RelativeTimeFormat` here on purpose: it is ~40 kB of ICU data behind a lazy
 * locale load in some engines, this page is English-only by design (the joke does not translate),
 * and the whole of this file is under a hundred lines.
 */

const MINUTE = 60
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * `1 h 30 min`, `45 min`, `2 d 3 h`, `18 s`.
 *
 * Two units at most, and never a trailing zero unit: "1 h 0 min" reads as a template that failed
 * to fill rather than as an hour.
 */
export function humanDuration(seconds: number): string {
  const total = clamp(seconds)
  if (total < MINUTE) return `${total} s`
  if (total < HOUR) return `${Math.floor(total / MINUTE)} min`
  if (total < DAY) return pair(Math.floor(total / HOUR), 'h', Math.floor((total % HOUR) / MINUTE), 'min')
  return pair(Math.floor(total / DAY), 'd', Math.floor((total % DAY) / HOUR), 'h')
}

/**
 * Whole, non-negative seconds — or zero.
 *
 * `Math.max(0, NaN)` is `NaN`, which is how a missing `durationS` becomes a banner reading
 * "NaN d NaN h" in front of everyone. The finite check has to come first.
 */
function clamp(seconds: number): number {
  return Number.isFinite(seconds) ? Math.max(0, Math.trunc(seconds)) : 0
}

function pair(big: number, bigUnit: string, small: number, smallUnit: string): string {
  return small === 0 ? `${big} ${bigUnit}` : `${big} ${bigUnit} ${small} ${smallUnit}`
}

/**
 * `1:02:33` / `4:07` — a running clock, zero-padded below the leading unit.
 *
 * Colons rather than words because this one is re-rendered every second and a label that changes
 * width every time it ticks makes the whole drawer jitter.
 */
export function liveDuration(seconds: number): string {
  const total = clamp(seconds)
  const s = total % MINUTE
  const m = Math.floor(total / MINUTE) % MINUTE
  const h = Math.floor(total / HOUR)
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}

/** `3 min ago`, `just now`, `2 h ago`. `null` for a timestamp that cannot be parsed. */
export function relativeTime(iso: string | null | undefined, now: number): string | null {
  const at = parseIso(iso)
  if (at === null) return null
  const seconds = Math.round((now - at) / 1000)
  if (seconds < 45) return 'just now'
  if (seconds < 0) return 'just now'
  return `${humanDuration(seconds)} ago`
}

/** The visitor's own clock and locale — this is the one place a local time is correct. */
export function absoluteTime(iso: string | null | undefined): string | null {
  const at = parseIso(iso)
  if (at === null) return null
  try {
    return new Date(at).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return null
  }
}

/**
 * Seconds since an ISO timestamp, or `null` when the server did not send one.
 *
 * Clamped at zero: a visitor whose clock is a minute fast would otherwise see a siege that has
 * been running for minus fifty-eight seconds, which reads as a broken page rather than as a
 * broken clock.
 */
export function secondsSince(iso: string | null | undefined, now: number): number | null {
  const at = parseIso(iso)
  return at === null ? null : Math.max(0, Math.floor((now - at) / 1000))
}

function parseIso(iso: string | null | undefined): number | null {
  if (typeof iso !== 'string' || iso === '') return null
  const at = Date.parse(iso)
  return Number.isFinite(at) ? at : null
}
